<script>
import {Icon} from '@iconify/vue';
import {required} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';

/* COMPOSANTS */
import AkInputText from '@components/input/AkInputText';
import AkInputMoney from '@components/input/AkInputMoney';
import AkDialog from '@components/general/AkDialog';
import GaView from '@components/layout/GaView';

/* MIXINS */
import randomRef from '@mixins/randomRef';
import utilsMixin from '@mixins/utilsMixin';
import loaderMixin from '@mixins/loaderMixin';
import messageMixin from '@mixins/messageMixin';

/* SERVICES */
import amountThresholdService from '@services/amountThresholdService';

export default {
  components: {
    Icon,
    AkDialog,
    AkInputText,
    AkInputMoney,
    GaView,
  },
  mixins: [loaderMixin, messageMixin, randomRef, utilsMixin],
  setup() {
    return {v$: useVuelidate()};
  },
  head() {
    return {
      title: 'Minimums de facturation',
    };
  },
  data() {
    return {
      list: [],
      item: {},
    };
  },
  validations() {
    return {
      item: {
        label: {required},
        amountGa: {required},
        amountGav: {required},
      },
    };
  },
  mounted() {
    this.getRef().showTotalLoader();
    amountThresholdService.amountThresholds().then((data) => {
      this.list = data;
      this.getRef().hideLoader();
    });
  },
  methods: {
    create() {
      this.v$.$touch();
      if (this.v$.$error) return;

      this.getRef().showTotalLoader();
      amountThresholdService
        .create(this.item)
        .then((data) => {
          this.list.push(data);
          this.item = {};
          this.getRef().success(this.$t('added'));
        })
        .finally(() => {
          this.getRef().hideLoader();
          this.$refs.dialogCreate.hide();
        });
    },

    update() {
      this.v$.$touch();
      if (this.v$.$error) return;

      this.getRef().showTotalLoader();
      amountThresholdService
        .update(this.item)
        .then((data) => {
          this.replaceInListById(this.list, data);
          this.getRef().success(this.$t('updated'));
        })
        .finally(() => {
          this.getRef().hideLoader();
          this.$refs.dialogEdit.hide();
        });
    },
    delete() {
      amountThresholdService
        .delete(this.item)
        .then((data) => {
          this.removeInListById(this.list, data.id);
          this.getRef().success(this.item.label + ' a bien été supprimé');
        })
        .catch((e) => {
          let content = 'Une erreur est survenue durant la suppression';
          if (e.response) {
            content = 'Une erreur est survenue durant la suppression : ' + e.response.data.error;
          }
          this.getRef().error(content);
        });
    },
    openCreateDialog() {
      this.item = {};
      this.$refs.dialogCreate.show();
    },
    openEditDialog(item) {
      this.item = {...item};
      this.$refs.dialogEdit.show();
    },
    openDeleteDialog(item) {
      this.item = item;
      this.$refs.dialogDelete.show();
    },
  },
};
</script>

<template>
  <GaView :ref="ref" title="Minimums de facturation">
    <template #action>
      <div class="d-flex justify-content-end">
        <button class="btn btn-primary float-right" @click="openCreateDialog()">
          <i class="gst-plus pr-1" />{{ $t('add') }}
        </button>
      </div>
    </template>

    <template #content>
      <DataTable
        :always-show-paginator="false"
        :paginator="true"
        :rows="10"
        :rows-per-page-options="[10, 20, 50]"
        :value="list"
        class="table"
        current-page-report-template="{first}-{last}/{totalRecords}"
        removable-sort
        responsive-layout="scroll"
        sort-field="position"
        striped-rows>
        <template #empty>
          {{ $t('stock.empty') }}
        </template>

        <Column :header="$t('amountThreshold.label')" field="label">
          <template #body="slotProps">
            {{ slotProps.data.label }}
          </template>
        </Column>

        <Column :header="$t('amountThreshold.amountGa')" field="label">
          <template #body="slotProps">
            {{ formatCurrencyEUR(formatAmount(slotProps.data.amountGa)) }}
          </template>
        </Column>

        <Column :header="$t('amountThreshold.amountGav')" field="label">
          <template #body="slotProps">
            {{ formatCurrencyEUR(formatAmount(slotProps.data.amountGav)) }}
          </template>
        </Column>

        <!--        <Column
            body-style="text-align: right; overflow: visible"
            header-style=" width: 160px; text-align: btn"
        >
          <template #body="slotProps">
            <div class="d-flex justify-content-end">
              <span
                  class="btn btn-xs btn-inverse-primary mr-1 pointer"
                  @click="openEditDialog(slotProps.data)"
              >
                <i class="fe fe-edit"/>
              </span>
              <span
                  class="btn btn-xs btn-inverse-danger pointer"
                  @click="openDeleteDialog(slotProps.data)"
              >
                <i class="fe fe-trash"/>
              </span>
            </div>
          </template>
        </Column>-->
      </DataTable>
    </template>

    <template #extra>
      <AkDialog
        ref="dialogCreate"
        :title="$t('add')"
        :validate-label="$t('amountThreshold.add')"
        width="450px"
        @validate="create()">
        <div class="form-row">
          <AkInputText
            v-model="item.label"
            :label="$t('amountThreshold.label')"
            :validator="v$.item.label"
            class-name="col-12" />
        </div>
        <div class="form-row">
          <AkInputMoney
            v-model="item.amountGa"
            :label="$t('amountThreshold.amountGa')"
            :validator="v$.item.amountGa"
            class-name="col-12" />
        </div>
        <div class="form-row">
          <AkInputMoney
            v-model="item.amountGav"
            :label="$t('amountThreshold.amountGav')"
            :validator="v$.item.amountGav"
            class-name="col-12" />
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogEdit"
        :title="$t('amountThreshold.update')"
        :validate-label="$t('update')"
        width="450px"
        @validate="update()">
        <div class="form-row">
          <AkInputText v-model="item.label" :label="$t('stock.label')" :validator="v$.item.label" class-name="col-12" />
        </div>
        <div class="form-row">
          <AkInputMoney
            v-model="item.amountGa"
            :label="$t('amountThreshold.amountGa')"
            :validator="v$.item.amountGa"
            class-name="col-12" />
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogDelete"
        :auto-hide-on-validate="true"
        :cancel-label="$t('no')"
        :title="$t('amountThreshold.delete')"
        :validate-label="$t('yes')"
        width="450px"
        @validate="this.delete()">
        <div class="flex items-center gap-2">
          <Icon icon="fluent:warning-20-regular" class="text-red" width="26" />
          <span>{{ $t('stock.confirm_delete') }}</span>
        </div>
      </AkDialog>
    </template>
  </GaView>
</template>
